/**
 * Resolves RTL (Right-to-Left) configuration based on the provided locale.
 *
 * This function determines whether the given locale should use RTL text direction
 * by comparing it against a list of known RTL locales. The comparison is case-insensitive
 * and supports matching on both full locale tags and base language subtags.
 *
 * @param locale - The resolved locale for the application (e.g., 'ar', 'ar-SA', 'he-IL')
 * @param rtlLocales - An array of locale codes that should be treated as RTL (e.g., ['ar', 'he', 'fa'])
 * @returns `true` if the locale matches an RTL locale, `false` otherwise
 *
 * @example
 * // Exact match on full locale tag
 * resolveRtlEnabled('ar-SA', ['ar-SA', 'he']); // returns true
 *
 * @example
 * // Match on base language subtag
 * resolveRtlEnabled('ar-EG', ['ar', 'ar-sa', 'he']); // returns true (ar-EG matches 'ar')
 *
 * @example
 * // Case-insensitive matching
 * resolveRtlEnabled('AR-SA', ['ar-sa']); // returns true
 *
 * @example
 * // No match returns false
 * resolveRtlEnabled('en-US', ['ar', 'ar-sa', 'he']); // returns false
 *
 * @example
 * // Empty or invalid inputs return false
 * resolveRtlEnabled('', ['ar']); // returns false
 * resolveRtlEnabled('ar', []); // returns false
 */
export function resolveRtlEnabled(locale: string, rtlLocales: readonly string[]): boolean {
  if (!locale || !rtlLocales?.length) {
    return false;
  }

  const normalizedLocale = locale.toLowerCase();
  const localeBase = normalizedLocale.split(/[-_]/)[0];

  for (const rtlLocale of rtlLocales) {
    if (!rtlLocale) {
      continue;
    }

    const normalizedRtlLocale = rtlLocale.toLowerCase();
    const rtlLocaleBase = normalizedRtlLocale.split(/[-_]/)[0];

    // Exact match on full locale tag (e.g., 'ar-sa' === 'ar-sa')
    if (normalizedLocale === normalizedRtlLocale) {
      return true;
    }

    // Match on base language subtag (e.g., 'ar' === 'ar-sa')
    if (localeBase === rtlLocaleBase) {
      return true;
    }
  }

  return false;
}

/**
 * Sets the document's `dir` attribute based on RTL enabled state.
 * @param rtlEnabled - Whether RTL layout is enabled
 */
export function setDocumentDirection(rtlEnabled: boolean): void {
  document.documentElement.setAttribute('dir', rtlEnabled ? 'rtl' : 'ltr');
}
